import React, { useContext, useEffect, useState } from "react";
import { ReportParamsContext } from "../../context/ReportParamsContext";
import { getEntities, getTaskReport, SERVER_API } from "../../API";

function normalizeList(response) {
  if (Array.isArray(response)) return response;
  if (response?._embedded) return Object.values(response._embedded).flat();
  if (response?.content) return response.content;
  return [];
}

function formatDate(epoch) {
  if (!epoch) return "";
  return new Date(epoch).toLocaleDateString("en-CA");
}

const SKIP_REASON_LABELS = {
  STORE_CLOSED:  "Store closed",
  NOT_ON_SHELF:  "Product not on shelf / delisted",
  NO_ACCESS:     "No access / refused entry",
  OUT_OF_TIME:   "Out of time",
  OTHER:         "Other",
};

function formatSkipReason(reason) {
  return reason ? (SKIP_REASON_LABELS[reason] ?? reason) : "";
}

function formatDuration(ms) {
  if (!ms) return "";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function TaskReport() {
  const [{ fromDate, toDate, representatives }] = useContext(ReportParamsContext);

  const [allTasks, setAllTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPhoto, setExpandedPhoto] = useState(null);

  useEffect(() => {
    getEntities("tasks", "?size=1000&sort=name,asc").then((r) => {
      const list = normalizeList(r);
      list.sort((a, b) => {
        if (a.status === b.status) return (a.name ?? "").localeCompare(b.name ?? "");
        return a.status === "ACTIVE" ? -1 : 1;
      });
      setAllTasks(list);
    });
  }, []);

  const handleGenerate = async () => {
    if (!selectedTaskId) {
      setError("Please select a task.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getTaskReport(Number(selectedTaskId), {
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        representativeIds: representatives.map((r) => r.id),
      });
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-grow-1 overflow-auto">
      <div className="container-fluid py-4 px-4">

        <div className="card shadow-sm mb-4">
          <div className="card-body p-4">
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label fw-medium mb-1">Task</label>
                <select
                  className="form-select"
                  value={selectedTaskId}
                  onChange={(e) => { setSelectedTaskId(e.target.value); setReport(null); }}
                >
                  <option value="">— Select a task —</option>
                  {allTasks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.status === "INACTIVE" ? " (Inactive)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-auto">
                <button
                  className="btn btn-primary"
                  onClick={handleGenerate}
                  disabled={loading || !selectedTaskId}
                >
                  {loading ? "Loading…" : "Generate Report"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {report && (
          <>
            <div className="text-muted small mb-2">
              {report.rows.length} visit{report.rows.length !== 1 ? "s" : ""}
            </div>
            <div className="table-responsive">
              <table
                className="table table-hover table-bordered mb-0"
                style={{ fontSize: "0.85rem" }}
              >
                <thead className="table-light">
                  <tr>
                    <th style={{ whiteSpace: "nowrap" }}>Date</th>
                    <th style={{ whiteSpace: "nowrap" }}>Representative</th>
                    <th style={{ minWidth: 160 }}>Address</th>
                    <th>City</th>
                    <th>Province</th>
                    <th style={{ whiteSpace: "nowrap" }}>Time Spent</th>
                    <th style={{ whiteSpace: "nowrap" }}>Status</th>
                    {report.questions.map((q) => (
                      <th
                        key={q.no}
                        style={{ minWidth: q.type === "PHOTO" ? 170 : 160 }}
                      >
                        {q.text}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7 + report.questions.length}
                        className="text-center text-muted py-4"
                      >
                        No data found for this task.
                      </td>
                    </tr>
                  ) : (
                    report.rows.map((row, i) => (
                      <tr key={i}>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDate(row.visitDate)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{row.representativeName}</td>
                        <td>{row.storeAddress}</td>
                        <td>{row.storeCity}</td>
                        <td>{row.storeProvince}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDuration(row.durationMs)}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          {row.status === "SKIPPED" ? (
                            <span>
                              <span className="badge bg-warning text-dark">Skipped</span>
                              <span className="ms-1 text-muted small">{formatSkipReason(row.skipReason)}</span>
                              {row.skipNotes && <div className="text-muted small fst-italic">{row.skipNotes}</div>}
                            </span>
                          ) : (
                            <span className="badge bg-success">Completed</span>
                          )}
                        </td>
                        {row.status === "SKIPPED"
                          ? report.questions.map((q) => <td key={q.no} className="text-muted">—</td>)
                          : report.questions.map((q) => {
                              const ans = row.answers?.[String(q.no)];
                              if (!ans) return <td key={q.no} />;
                              if (ans.type === "PHOTO" && ans.fileUrl) {
                                return (
                                  <td key={q.no}>
                                    <img
                                      src={`${SERVER_API}${ans.fileUrl}`}
                                      alt=""
                                      style={{
                                        width: 160,
                                        height: 90,
                                        objectFit: "contain",
                                        cursor: "pointer",
                                        borderRadius: 4,
                                        background: "#f0f0f0",
                                      }}
                                      onClick={() =>
                                        setExpandedPhoto(`${SERVER_API}${ans.fileUrl}`)
                                      }
                                    />
                                  </td>
                                );
                              }
                              return <td key={q.no}>{ans.value ?? ""}</td>;
                            })
                        }
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {expandedPhoto && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: "rgba(0,0,0,0.85)", zIndex: 9999 }}
            onClick={() => setExpandedPhoto(null)}
          >
            <img
              src={expandedPhoto}
              alt=""
              style={{
                maxHeight: "90vh",
                maxWidth: "90vw",
                objectFit: "contain",
                borderRadius: 6,
              }}
            />
          </div>
        )}

      </div>
    </div>
  );
}
